/**
 * Shared File Upload & Validation Configuration for Frontend
 * Synchronized with Backend File Validation (multerConfig.js & fileUploadConfig.js)
 */

export const ALLOWED_IMAGE_MIME_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
] as const;

export const ALLOWED_IMAGE_EXTENSIONS = [
  'jpg',
  'jpeg',
  'png',
  'gif',
  'webp',
] as const;

/**
 * Standard HTML file input `accept` attribute string.
 * Specifies both MIME types and file extensions for complete cross-browser & OS file picker alignment.
 */
export const IMAGE_ACCEPT_ATTRIBUTE =
  'image/jpeg,image/png,image/gif,image/webp,.jpg,.jpeg,.png,.gif,.webp';

export interface ImageValidationOptions {
  maxSizeMB?: number;
  label?: string;
}

export interface ValidationResult {
  valid: boolean;
  error?: string;
}

/**
 * Validates an image file by checking file size, MIME type, and file extension (case-insensitive).
 */
export const validateImageFile = (
  file: File | null | undefined,
  options: ImageValidationOptions = {}
): ValidationResult => {
  if (!file) {
    return { valid: false, error: 'No file selected' };
  }

  const { maxSizeMB = 2, label = 'Image' } = options;

  // 1. File Size Validation
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    return {
      valid: false,
      error: `${label} should be smaller than ${maxSizeMB}MB.`,
    };
  }

  // 2. File Type / Extension Validation (case-insensitive)
  const mimeType = (file.type || '').toLowerCase().trim();
  const extension = (file.name || '').split('.').pop()?.toLowerCase().trim() || '';

  const isMimeAllowed = (ALLOWED_IMAGE_MIME_TYPES as readonly string[]).includes(mimeType);
  const isExtAllowed = (ALLOWED_IMAGE_EXTENSIONS as readonly string[]).includes(extension);

  if (!isMimeAllowed && !isExtAllowed) {
    return {
      valid: false,
      error: 'Invalid file type. Only JPEG, PNG, GIF, and WebP are allowed',
    };
  }

  return { valid: true };
};
